import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import * as errorHandlerActions from '@/shared/modules/errorHandler/actions';
import config from '@/config';
import { processJuniorHrefRequest } from '@/shared/services/api';
import * as gameActions from '../store/actions';
import { gameActionTypes } from '../store/constants';
import { normalizeJuniorGameData } from './juniorGameData';

import type { SagaIterator } from 'redux-saga';
import type { RootState } from '@/rootState';
import type { Href } from '@/shared/utils/href';
import type { JuniorGameResponse } from './juniorGameData';

export default function* (): Generator {
	yield all([
		takeLatest([gameActionTypes.GET_GAME_DATA_REQUEST, gameActionTypes.CLEAR_GAME_STATE], handleGetGameDataRequest),
		takeLatest(gameActionTypes.FINISH_GAME_REQUEST, handleFinishGameRequest),
	]);
}

export function* handleGetGameDataRequest(action: { type: string }): SagaIterator {
	if (action.type === gameActionTypes.CLEAR_GAME_STATE) return;

	try {
		if (config.game_config_error) {
			throw new Error(config.game_config_error);
		}

		const dataHref = config.data_href;

		if (!dataHref?.href) {
			throw new Error('Game data config is missing');
		}

		const response: { data: JuniorGameResponse } = yield call(processJuniorHrefRequest, dataHref);
		const { gameData, sessionEndHref } = normalizeJuniorGameData(response.data);
		yield put(gameActions.getGameDataSuccess(gameData, sessionEndHref));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(gameActions.getGameDataError());
	}
}

export function* handleFinishGameRequest({
	payload,
	meta,
}: ReturnType<typeof gameActions.finishGameRequest>): SagaIterator {
	const sessionEndHref: Href | undefined = yield select((state: RootState) => state.gameReducer.sessionEndHref);

	try {
		if (sessionEndHref?.href) {
			yield call(processJuniorHrefRequest, sessionEndHref, payload);
		}

		yield put(gameActions.finishGameSuccess());
	} catch (e) {
		yield put(gameActions.finishGameError());
		yield put(errorHandlerActions.handleError(e, 'notification'));
	} finally {
		meta.onFinished?.();
	}
}
